<template>
    <div class="wrapper" :class="{ 'nav-open': $sidebar.showSidebar, 'auto-mode': $store.autoModeEnabled }" :style="{ '--sidebar-bg': sidebarBgColor }">

        <button @click="forceShowSidebar = !forceShowSidebar"
            :class="{ 'mobile-nav-toggle': true, force: forceShowSidebar }">
            <i class="now-ui-icons text_align-center visible-on-sidebar-regular" />
        </button>

        <notifications />

        <side-bar ref="sidebar" :backgroundColor="sidebarColor" :environmentLabel="environmentLabel" :environmentBgColor="environmentBannerColor" :phoneDisconnected="disconnected"
            :phoneConnecting="reconnecting" :phoneStatsEnabled="phoneStatsEnabled" :force-show="forceShowSidebar"
            @toggle-force="forceShowSidebar = !forceShowSidebar">
            <template #links>
                <!--<user-menu />-->

                <div class="px-3 filter-bar-container" data-tour="sidebar.filter">
                    <b-input ref="navFilterInput" type="search" placeholder="Filter Navigation" v-model="navQuery" class="form-control-sm"
                        @keydown.enter.native="navFilterSubmit"
                        @keydown.down.native.prevent="navigateFilteredItems('down')"
                        @keydown.up.native.prevent="navigateFilteredItems('up')"
                        @keydown.esc.native.prevent="handleFilterEscape"
                        @focus.native="handleFilterFocus"
                        @blur.native="handleFilterBlur" />
                </div>

                <sidebar-item v-for="(i, index) in filteredSidebarItems" :key="i.path" :link="i" :force-open="!!navQuery"
                    :class="{ 'nav-selected': isParentSelected(index) }"
                    @link-click="forceShowSidebar = false">
                    <template v-if="i.children?.length">
                        <sidebar-item v-for="(si, childIndex) in i.children" :key="si.path" :link="si"
                            :favorites="favoriteItems"
                            :class="{ 'nav-selected': isChildSelected(index, childIndex) }"
                            @link-click="forceShowSidebar = false" @toggle-favorite="updateNavFavorites" />
                    </template>
                    <template v-else-if="i.name == 'Favorites'">
                        <div style="color: white; border: 2px dashed white; border-radius: 6px;"
                            class="mx-2 px-3 py-1 text-center">
                            <small>
                                To add or remove favorites,
                                <template v-if="isMacOs">⌘</template>
                                <template v-else>CTRL</template>
                                + Click sidebar navigation items.
                            </small>

                            <b-button @click="favoritesToFalse" size="sm">
                                Dismiss
                            </b-button>
                        </div>
                    </template>
                </sidebar-item>
            </template>
        </side-bar>
        <div class="main-panel">
            <top-navbar ref="topNavbar" />
            <router-view name="header" />

            <div :class="{ content: !$route.meta.hideContent }" @click="toggleSidebar">
                <!-- out-in creates the next page only once the old one has left, so keep the leave at 1 ms (0 reads as unset) -->
                <zoom-center-transition :duration="{ enter: 200, leave: 1 }" mode="out-in">
                    <!-- your content here -->
                    <router-view />
                </zoom-center-transition>
            </div>
            <content-footer v-if="!$route.meta.hideFooter" help-links />

            <!-- Resolved from the lazy tour chunk on first use, then kept. Opened by binding
                 `visible`, not by calling show() on a ref: the component is rendered and asked to
                 open in the same breath, and Bootstrap Vue's modal manager has not necessarily
                 registered the id by the next tick. -->
            <component :is="tourHubComponent" v-if="tourHubComponent"
                :sandbox-mode="isSandboxMode" :visible.sync="tourHubOpen" @run="onRunTour" />
        </div>
    </div>
</template>

<script lang="ts">
/* eslint-disable no-new */
import PerfectScrollbar from "perfect-scrollbar";
import "perfect-scrollbar/css/perfect-scrollbar.css";

function hasElement(className: string) {
    return document.getElementsByClassName(className).length > 0;
}

function initScrollbar(className: string) {
    if (hasElement(className)) {
        new PerfectScrollbar(`.${className}`);
    } else {
        // try to init it later in case this component is loaded async
        setTimeout(() => {
            initScrollbar(className);
        }, 100);
    }
}

import TopNavbar from "./TopNavbar.vue";
import ContentFooter from "./ContentFooter.vue";
//import UserMenu from "./Extra/UserMenu.vue";
import { ZoomCenterTransition } from "vue2-transitions";
import { Component, Vue, Watch } from "vue-property-decorator";
import { markRaw } from "vue";
import { PhoneStats } from "@/models/PhoneStats";

import SideBar from "@/components/SidebarPlugin/SideBar.vue";
import SidebarItem from "@/components/SidebarPlugin/SidebarItem.vue";
import { getSidebarItems, NavigationItem } from "@/config/navigationItems";
import { isNonProduction as checkNonProduction, getEnvironmentSidebarColor, getEnvironmentBgColor, getEnvironmentBannerColor } from "@/util/environment";
import { isTenantConfigLoaded, loadTenantConfig, tenantConfig, tenantFlag } from "@/services/tenantConfigService";
import { getWelcomeShownOn, isWelcomeDismissed, shouldAutoShowWelcome, WelcomeGateInput } from "@/services/welcomeService";
import { isGuidedTourDisabled, isWelcomeDisabled } from "@/services/onboardingRollout";
import { buildTourContext } from "@/tour/context";
import { TourHost, TourModule, tourAnchorSelector } from "@/tour/types";
import { exitAutoMode as exitAutoModeSession } from "@/util/autoMode";
import { handleChunkLoadFailure, isChunkLoadError } from "@/util/updateCheck";
// Type-only, so nothing from the tour chunk is pulled into the main bundle.
import type { TourService } from "@/tour/TourService";

@Component({
    components: {
        TopNavbar,
        ContentFooter,
        //UserMenu,
        ZoomCenterTransition,
        SideBar,
        SidebarItem
    },
})
export default class Dashboard extends Vue implements TourHost {

    protected navQuery?: string = "";
    protected forceShowSidebar: boolean = false;
    protected selectedNavIndex: number = -1;
    protected selectedNavParentIndex: number = -1;
    protected selectedNavChildIndex: number = -1;
    protected filterHasFocus: boolean = false;

    /** One auto-show attempt per page load; localStorage carries the guard across reloads. */
    private welcomeGateFired = false;

    mounted() {
        const docClasses = document.body.classList;
        const isWindows = navigator.platform.startsWith("Win");
        if (isWindows) {
            // if we are on windows OS we activate the perfectScrollbar function
            initScrollbar("sidebar");
            initScrollbar("sidebar-wrapper");

            docClasses.add("perfect-scrollbar-on");
        } else {
            docClasses.add("perfect-scrollbar-off");
        }

        // Listen for sidebar minimize state changes
        if (this.$sidebar && this.$sidebar.eventBus) {
            this.$sidebar.eventBus.$on('sidebar-minimize-changed', this.handleMinimizeChange);
        }
        
        // Load and apply saved minimize state
        this.loadSavedMinimizeState();

        // Only the configuration flags that gate config-driven nav items (e.g. Accounts
        // Receivable), plus the onboarding toggles the footer and the gate below read.
        loadTenantConfig();

        // The footer hosts both links but the run belongs here, so the two talk over the root.
        this.$root.$on("tour:open-hub", this.onOpenTourHub);
        this.$root.$on("tour:start-walkthrough", this.onStartWalkthrough);
    }

    private menuItemHiddenByConfig(item: any, parent?: any): boolean {
        const flag = item.requiresConfigFlag || parent?.requiresConfigFlag;
        if (!flag) return false;
        return !tenantConfig()[flag];
    }

    /**
     * Everything the welcome gate decides on. A computed so one watcher covers all of it, and so
     * the decision itself stays in welcomeService as a pure function.
     */
    private get welcomeGateInput(): WelcomeGateInput {
        return {
            tenantConfigLoaded: isTenantConfigLoaded(),
            welcomeDisabled: isWelcomeDisabled(),
            sandboxMode: tenantFlag("sandboxMode"),
            permissionCount: this.$permissions.length,
            userSettingsLoaded: this.$root.$data.userSettingsLoaded === true,
            dismissed: isWelcomeDismissed(),
            firstLoginDate: this.$user?.firstLoginDate ?? null,
            shownOn: getWelcomeShownOn(this.$user?.userName),
            // Read here so the watcher re-runs on a navigation to a page that has one.
            footerShown: !this.$route.meta?.hideFooter,
            now: new Date(),
        };
    }

    @Watch("welcomeGateInput", { immediate: true })
    onWelcomeGateInputChanged() {
        if (this.welcomeGateFired) return;
        if (!shouldAutoShowWelcome(this.welcomeGateInput)) return;

        this.welcomeGateFired = true;
        // Deferred a tick because an immediate watcher can run before the footer that hosts the
        // modal has registered its listener.
        this.$nextTick(() => this.$root.$emit("welcome:auto-show"));
    }

    // ---- Guided tour -------------------------------------------------------
    // The layout owns the run because it owns the chrome a tour points at: the sidebar, the top
    // nav and the footer. It is also the only component guaranteed to outlive one.

    /** The hub's component definition, resolved from the tour chunk on first use. */
    protected tourHubComponent: object | null = null;
    protected tourHubOpen = false;
    private tourService: TourService | null = null;

    get isSandboxMode(): boolean {
        return tenantFlag("sandboxMode");
    }

    /**
     * Loads the tour chunk and builds the service, once. Every entry point goes through here, so
     * the dynamic import and its failure handling exist in exactly one place.
     */
    private async loadTour(): Promise<typeof import("@/tour/index") | null> {
        try {
            const tour = await import("@/tour/index");

            // markRaw: nothing renders from the service, and made reactive Vue would walk all of
            // it -- the router, the planned steps and the driver.js instance included.
            this.tourService ??= markRaw(new tour.TourService({
                host: this,
                // Rebuilt per call: permissions and the tenant row can land mid-session.
                ctx: () => buildTourContext(this),
                router: this.$router,
                setActive: (active: boolean) => { this.$root.$data.tourActive = active; },
                // Finishing a walkthrough hands the user back to the list, rather than leaving
                // them on whatever page the last step ended on with nothing to go on with.
                onFinished: () => { this.tourHubOpen = true; },
            }));
            this.tourHubComponent ??= tour.TourHubModal;

            return tour;
        } catch (error) {
            // This tab's bundle predates the current deploy, so the chunk URL is gone.
            if (isChunkLoadError(error)) handleChunkLoadFailure();
            else console.error("Guided tour: could not load", error);
            return null;
        }
    }

    /** The footer's Guided Tour link. */
    protected async onOpenTourHub() {
        if (isGuidedTourDisabled()) return;
        if (!await this.loadTour()) return;

        // Rendering it open is all that is needed; there is no ref and no timing to get wrong.
        this.tourHubOpen = true;
    }

    /**
     * The welcome modal's tour button. A user on their first day gets the basics walkthrough and
     * nothing else; the hub opens on its own when that finishes, so what they see next is their
     * choice. Anyone who has already done basics goes straight to the hub.
     *
     * `welcomeStartFrom` owns the rule -- see it for why the whole catalogue back to back is not
     * what this button does any more.
     */
    protected async onStartWalkthrough() {
        if (isGuidedTourDisabled()) return;
        const tour = await this.loadTour();
        if (!tour) return;

        const intro = tour.welcomeStartFrom(tour.getEligibleModules(buildTourContext(this)));
        if (intro) this.tourService?.runModule(intro);
        else this.tourHubOpen = true;
    }

    /** Whatever the hub's buttons chose. */
    protected onRunTour(modules: TourModule[], chain: boolean) {
        if (!modules.length) return;
        if (chain) this.tourService?.runChain(modules);
        else this.tourService?.runModule(modules[0]);
    }

    /**
     * Retries a configuration fetch that failed at mount. Once one has succeeded this does nothing
     * -- the service keeps the settled promise -- so the cost is a function call per navigation.
     */
    @Watch("$route")
    onRouteChanged() {
        loadTenantConfig();
    }

    @Watch("$root.data.dataHubState") onConnectionStatusChange() {
        console.log("dataHubStatus = " + this.$root.$data.dataHubState);
    }

    @Watch("sidebarMinimizedSetting")
    onMinimizeSettingChanged(newValue: string | undefined) {
        if (newValue !== undefined && this.$sidebar) {
            const shouldBeMinimized = newValue === 'true';
            const currentlyMinimized = this.$sidebar.isMinimized;
            
            if (shouldBeMinimized !== currentlyMinimized) {
                console.log(`Syncing sidebar minimize state from ${currentlyMinimized} to ${shouldBeMinimized}`);
                this.$sidebar.setMinimizeState(shouldBeMinimized, false);
            }
        }
    }

    @Watch("$root.$data.userSettings", { immediate: true })
    onUserSettingsLoaded() {
        this.loadSavedMinimizeState();
    }

    get savedFavoritesSetting(): string | undefined {
        return (this.$root as any).$data.userSettings?.['nav-favorites'];
    }

    @Watch("savedFavoritesSetting", { immediate: true })
    onSavedFavoritesSettingChanged() {
        this.loadSavedFavorites();
    }

    get isNonProduction(): boolean {
        return checkNonProduction(this.$environment);
    }

    get environmentLabel(): string {
        if (!this.isNonProduction) return '';
        return (this.$environment || 'Unknown').toUpperCase();
    }

    get sidebarColor(): string {
        return getEnvironmentSidebarColor(this.$environment);
    }

    get sidebarBgColor(): string {
        return getEnvironmentBgColor(this.$environment);
    }

    get environmentBannerColor(): string {
        return getEnvironmentBannerColor(this.$environment);
    }

    get disconnected(): boolean {
        return this.$root.$data.dataHubState == "Disconnected" || this.$root.$data.dataHubState == "Closed";
    }

    get reconnecting(): boolean {
        return this.$root.$data.dataHubState == "Connecting" || this.$root.$data.dataHubState == "Reconnecting";
    }

    get phoneStatsEnabled(): boolean | null {
        return this.$root.$data.phoneStatsEnabled;
    }

    toggleSidebar() {
        if ((this.$refs?.sidebar as any)?.showSidebar) {
            (this.$refs!.sidebar as any).displaySidebar(false);
        }
    }

    get printQueueName() {
        return this.$pageNames.printQueue ?? "Print Queue";
    }

    get stalePrintQueueName() {
        return this.$pageNames.stalePrintQueue ?? "Stale Print Queue";
    }

    get sidebarItems(): NavigationItem[] {
        return getSidebarItems(this.printQueueName, this.stalePrintQueueName);
    }

    get allSidebarItemChildren() {
        return this.sidebarItems.map(i => i.children).flat();
    }

    get allSidebarItems() {

        if (this.favoriteItems === false) return this.sidebarItems;

        const favorites = this.favoriteItems.map((fi: any) => {
            const path = fi.path ? fi.path : fi;

            const cat = this.sidebarItems.find(sc => sc.children?.find((c: any) => c.path === path));
            const navItem = cat?.children?.find((i: any) => i.path === path);

            const hasMultipleSameName = this.allSidebarItemChildren.filter(i => i?.name?.toLowerCase() === navItem?.name?.toLowerCase()).length > 1;

            return {
                ...navItem,
                subtitle: hasMultipleSameName ? cat?.name : undefined,
            };
        });

        return [{ name: "Favorites", children: favorites, icon: "fas fa-heart" }, ...this.sidebarItems];
    }

    @Watch("navQuery")
    resetSidebarScrollPosition() {
        const sidebar = (this.$refs?.sidebar as any)?.$el.querySelector('.sidebar-wrapper');
        if (!sidebar) return;
        sidebar.scrollTop = 0;
        this.clearNavSelection();
    }

    get permissionedSidebarItems() {
        const permissionedItems: any[] = [];

        this.allSidebarItems.forEach(i => {
            if (this.menuItemMissingPermission(i) || this.menuItemHiddenByConfig(i)) return;

            const hasChildren = !!i.children?.length;

            if (!hasChildren) {
                permissionedItems.push(i);
                return;
            }

            const isFavorites = i.name === "Favorites";

            const permissionedChildren = i.children?.filter((ci: any) => !this.menuItemMissingPermission(ci, i) && !this.menuItemHiddenByConfig(ci, i));

            if (!permissionedChildren?.length) {
                if (isFavorites) permissionedItems.push({ ...i, children: [] });
                return;
            }

            permissionedItems.push({ ...i, children: permissionedChildren });
        });

        return permissionedItems;
    }

    get filteredSidebarItems() {
        if (!this.navQuery) return this.permissionedSidebarItems;

        const filteredItems: any = [];

        this.permissionedSidebarItems.forEach(i => {
            if (this.menuItemHasFilterMatch(i)) {
                filteredItems.push(i);
                return;
            }

            const filteredChildren = i.children?.filter((ci: any) => this.menuItemHasFilterMatch(ci, i));

            if (!filteredChildren?.length) return;

            filteredItems.push({ ...i, children: filteredChildren });
        });

        return filteredItems;
    }

    get navQueryTerms() {
        return this.navQuery?.toLowerCase().split(" ");
    }

    private menuItemMissingPermission(item: any, parent?: any) {
        if (item.name === "Favorites") return false;
        if (item.requiresAnyPermission && !this.$permissions.length) return true;
        const hasPermissionDefined = item.permission || parent?.permission;
        const hasAnyPermissionDefined = item.anyPermission || parent?.anyPermission;

        if (!hasPermissionDefined && !hasAnyPermissionDefined) return false;

        if (hasPermissionDefined) {
            return !this.$permissions.some(p => p.permissionCodeName.toLowerCase() === item.permission.toLowerCase());
        }

        return !this.$permissions.some(p => item.anyPermission.some((ap: string) => ap.toLowerCase() === p.permissionCodeName.toLowerCase()));
    }

    private menuItemHasFilterMatch(item: any, parent?: any) {
        return this.navQueryTerms?.every(t => (item.name + parent?.name + item.keywords + item.name?.match(/\b(\w)/g).join('')).toLowerCase().includes(t));
    }

    protected favoriteItems: any = [];

    updateNavFavorites(favorites: any) {
        this.favoriteItems = favorites;
        this.saveFavorites(favorites);
    }

    favoritesToFalse() {
        this.favoriteItems = false;
        this.saveFavorites(false);
    }

    private saveFavorites(favorites: any) {
        this.saveUserSetting('nav-favorites', JSON.stringify(favorites));
    }

    private saveUserSetting(key: string, value: string) {
        (this as any).$saveUserSetting?.(key, value);
        this.$set((this.$root as any).$data.userSettings, key, value);
    }

    loadSavedFavorites() {
        const savedFavorites = this.savedFavoritesSetting;
        if (savedFavorites !== undefined) {
            // Feedback loop guard: skip if we already have this value
            if (savedFavorites === JSON.stringify(this.favoriteItems)) return;
            this.favoriteItems = JSON.parse(savedFavorites);
        } else {
            // Fall back to localStorage for migration
            const localFavorites = window.localStorage.getItem("nav-favorites");
            if (localFavorites) {
                this.favoriteItems = JSON.parse(localFavorites);
                // Migrate to userSettings and clean up localStorage
                this.saveFavorites(this.favoriteItems);
                localStorage.removeItem("nav-favorites");
            }
        }
    }

    handleMinimizeChange(isMinimized: boolean) {
        this.saveUserSetting('sidebar-minimized', isMinimized.toString());
    }

    loadSavedMinimizeState() {
        // A tour opens the sidebar for its sidebar steps and puts the user's own setting back at
        // the end. Re-applying the saved value here would close it mid-step -- and a tour does
        // trigger this watcher, because completing a module writes its progress to user settings.
        if (this.$root.$data.tourActive) return;

        const userSettings = (this.$root as any).$data.userSettings || {};
        const savedMinimizedState = userSettings['sidebar-minimized'];
        
        if (savedMinimizedState !== undefined) {
            const isMinimized = savedMinimizedState === 'true';
            if (this.$sidebar && this.$sidebar.setMinimizeState) {
                this.$sidebar.setMinimizeState(isMinimized, true);
            }
        }
    }

    navFilterSubmit() {
        if (!this.navQuery?.trim() && this.selectedNavIndex === -1) return;
        
        // If we have a selected item, navigate to it
        if (this.selectedNavParentIndex >= 0) {
            const parent = this.filteredSidebarItems[this.selectedNavParentIndex];
            if (this.selectedNavChildIndex >= 0 && parent.children?.[this.selectedNavChildIndex]) {
                this.$router.push(parent.children[this.selectedNavChildIndex].path);
                this.clearNavSelection();
                this.blurNavFilter();
                this.focusMainContent();
                return;
            } else if (parent.path) {
                this.$router.push(parent.path);
                this.clearNavSelection();
                this.blurNavFilter();
                this.focusMainContent();
                return;
            }
        }
        
        // Otherwise navigate to first item as before
        const first = this.filteredSidebarItems[0];
        if (first?.children?.length) {
            this.$router.push(first.children[0].path);
        } else if (first?.path) {
            this.$router.push(first.path);
        }
        this.clearNavSelection();
        this.blurNavFilter();
        this.focusMainContent();
    }

    navigateFilteredItems(direction: 'up' | 'down') {
        const allItems = this.getAllNavigableItems();
        if (allItems.length === 0) return;

        let currentIndex = this.selectedNavIndex;
        
        if (direction === 'down') {
            currentIndex = currentIndex < allItems.length - 1 ? currentIndex + 1 : 0;
        } else {
            currentIndex = currentIndex > 0 ? currentIndex - 1 : allItems.length - 1;
        }
        
        this.selectedNavIndex = currentIndex;
        const item = allItems[currentIndex];
        if (item) {
            this.selectedNavParentIndex = item.parentIndex;
            this.selectedNavChildIndex = item.childIndex;
            this.scrollToSelectedItem();
        }
    }

    getAllNavigableItems() {
        const items: any[] = [];
        
        this.filteredSidebarItems.forEach((parent, parentIndex) => {
            // Add parent if it has a path (is navigable)
            if (parent.path && parent.name !== 'Favorites') {
                items.push({
                    parentIndex,
                    childIndex: -1,
                    element: parent
                });
            }
            
            // Add children
            if (parent.children?.length) {
                parent.children.forEach((child: any, childIndex: number) => {
                    items.push({
                        parentIndex,
                        childIndex,
                        element: child
                    });
                });
            }
        });
        
        return items;
    }

    isParentSelected(index: number): boolean {
        return this.filterHasFocus && this.selectedNavParentIndex === index && this.selectedNavChildIndex === -1;
    }

    isChildSelected(parentIndex: number, childIndex: number): boolean {
        return this.filterHasFocus && this.selectedNavParentIndex === parentIndex && this.selectedNavChildIndex === childIndex;
    }

    clearNavSelection() {
        this.selectedNavIndex = -1;
        this.selectedNavParentIndex = -1;
        this.selectedNavChildIndex = -1;
    }

    handleFilterEscape() {
        if (this.navQuery) {
            this.navQuery = '';
            this.clearNavSelection();
        } else {
            this.blurNavFilter();
            this.focusMainContent();
        }
    }

    blurNavFilter() {
        (this.$refs.navFilterInput as any)?.$el?.querySelector('input')?.blur();
    }

    handleFilterFocus() {
        this.filterHasFocus = true;
    }

    handleFilterBlur() {
        this.filterHasFocus = false;
        // Clear selection when losing focus
        this.clearNavSelection();
    }

    focusMainContent() {
        // Focus the main content area or first focusable element
        const mainPanel = document.querySelector('.main-panel .content');
        if (mainPanel) {
            // Try to find a focusable element in the main content
            const focusable = mainPanel.querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])') as HTMLElement;
            if (focusable) {
                focusable.focus();
            } else {
                // If no focusable element, make the content area focusable temporarily and focus it
                (mainPanel as HTMLElement).setAttribute('tabindex', '-1');
                (mainPanel as HTMLElement).focus();
                // Remove tabindex after focus to not interfere with normal tab navigation
                setTimeout(() => {
                    (mainPanel as HTMLElement).removeAttribute('tabindex');
                }, 0);
            }
        }
    }

    scrollToSelectedItem() {
        // Scroll the selected item into view, accounting for the sticky filter bar
        this.$nextTick(() => {
            const selectedElement = document.querySelector('.sidebar .nav-selected');
            const sidebarWrapper = document.querySelector('.sidebar-wrapper');
            const filterBar = document.querySelector('.filter-bar-container');
            
            if (selectedElement && sidebarWrapper && filterBar) {
                const filterBarHeight = filterBar.offsetHeight;
                const buffer = 10; // Small buffer for visual separation
                const requiredOffset = filterBarHeight + buffer;
                
                const sidebarRect = sidebarWrapper.getBoundingClientRect();
                const selectedRect = selectedElement.getBoundingClientRect();
                
                // Check if element is hidden behind filter bar or needs scrolling
                const elementTopRelativeToSidebar = selectedRect.top - sidebarRect.top;
                const elementBottomRelativeToSidebar = selectedRect.bottom - sidebarRect.top;
                
                let scrollTop = sidebarWrapper.scrollTop;
                
                // If element is hidden behind filter bar, scroll it into view below the filter bar
                if (elementTopRelativeToSidebar < requiredOffset) {
                    scrollTop = sidebarWrapper.scrollTop - (requiredOffset - elementTopRelativeToSidebar);
                }
                // If element is below visible area, scroll it into view
                else if (elementBottomRelativeToSidebar > sidebarRect.height) {
                    scrollTop = sidebarWrapper.scrollTop + (elementBottomRelativeToSidebar - sidebarRect.height);
                }
                
                // Only scroll if needed
                if (scrollTop !== sidebarWrapper.scrollTop) {
                    sidebarWrapper.scrollTo({ top: scrollTop, behavior: 'smooth' });
                }
            }
        });
    }

    get isMacOs() {
        return navigator?.userAgent?.indexOf("Mac") > -1;
    }

    get sidebarMinimizedSetting(): string | undefined {
        return (this.$root as any).$data.userSettings?.['sidebar-minimized'];
    }

    // ---- TourHost ----------------------------------------------------------
    // Everything a step may need done to the page before it can be shown. A module author calls
    // these through the host rather than touching the DOM or the sidebar store themselves.

    /** The sidebar state to put back when a run ends. Null while no run is going. */
    private sidebarSnapshot: { minimized: boolean; forceShow: boolean } | null = null;

    snapshotLayout(): void {
        this.sidebarSnapshot = {
            minimized: this.$sidebar?.isMinimized === true,
            forceShow: this.forceShowSidebar,
        };
    }

    restoreLayout(): void {
        const snapshot = this.sidebarSnapshot;
        this.sidebarSnapshot = null;
        if (!snapshot) return;

        // skipAnimation, and setMinimizeState does not raise sidebar-minimize-changed, so putting
        // the sidebar back does not write the user's setting.
        if (this.$sidebar?.isMinimized !== snapshot.minimized) {
            this.$sidebar?.setMinimizeState(snapshot.minimized, true);
        }
        this.forceShowSidebar = snapshot.forceShow;
    }

    openSidebar(): void {
        // A run cannot start below 992px, where the sidebar is off-canvas, so un-minimizing is
        // the only case that arises.
        if (this.$sidebar?.isMinimized) this.$sidebar.setMinimizeState(false, true);
    }

    revealNavItem(path: string): void {
        const item = document.querySelector(tourAnchorSelector("sidebar.item", path)) as HTMLElement | null;
        if (!item) return;

        // Start from the parent so the item cannot match itself as its own group.
        const group = item.parentElement?.closest(tourAnchorSelector("sidebar.item"));
        const toggle = group?.querySelector("a.sidebar-menu-item") as HTMLElement | null;
        if (toggle?.getAttribute("aria-expanded") === "false") toggle.click();

        // Default behaviour, not smooth: a scroll still running would move the anchor out from
        // under the popover driver.js has just positioned.
        item.scrollIntoView({ block: "center" });
    }

    focusGlobalSearch(): void {
        const navbar = this.$refs.topNavbar as any;
        navbar?.focusGlobalSearch?.();
    }

    async open(anchor: string, anchorKey?: string): Promise<void> {
        const trigger = document.querySelector(tourAnchorSelector(anchor, anchorKey)) as HTMLElement | null;
        trigger?.click();
        await this.$nextTick();
    }

    close(anchor: string, anchorKey?: string): void {
        // Clicking the same trigger again: every `open` target is a toggle -- a dropdown, a tab or
        // a collapse -- so this is the reverse of open for all of them.
        const trigger = document.querySelector(tourAnchorSelector(anchor, anchorKey)) as HTMLElement | null;
        trigger?.click();
    }

    exitAutoMode(): void {
        exitAutoModeSession(this);
    }

    watchInactivity(onLeaveActive: () => void): () => void {
        return this.$watch(
            () => this.$root.$data.inactivityState?.phase ?? "active",
            (phase: string) => { if (phase !== "active") onLeaveActive(); }
        );
    }

    beforeDestroy() {
        if (this.$sidebar && this.$sidebar.eventBus) {
            this.$sidebar.eventBus.$off('sidebar-minimize-changed', this.handleMinimizeChange);
        }
        this.$root.$off("tour:open-hub", this.onOpenTourHub);
        this.$root.$off("tour:start-walkthrough", this.onStartWalkthrough);
        // Nothing else would put the page back if the layout goes away mid-run.
        this.tourService?.stop();
    }
}
</script>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>

<style scoped lang="scss">
$scaleSize: 0.95;

@keyframes zoomIn95 {
    from {
        opacity: 0;
        transform: scale3d($scaleSize, $scaleSize, $scaleSize);
    }

    to {
        opacity: 1;
    }
}

.main-panel .zoomIn {
    animation-name: zoomIn95;
}

@keyframes zoomOut95 {
    from {
        opacity: 1;
    }

    to {
        opacity: 0;
        transform: scale3d($scaleSize, $scaleSize, $scaleSize);
    }
}

.main-panel .zoomOut {
    animation-name: zoomOut95;
}

::v-deep(.sidebar input) {
    background: black;
    border: 1px solid black;
    color: white;

    &:hover,
    &:active,
    &:focus {
        border: 1px solid gray;
        background: black;
        color: white;
    }
}

.mobile-nav-toggle {
    position: fixed;
    top: 5px;
    left: 5px;
    z-index: 1100;
    border-radius: 50%;
    padding: 11px;
    border: 1px solid white;
    background: rgba(0, 0, 0, .65);
    color: white;
    display: flex;
    -webkit-backdrop-filter: blur(5px);
    backdrop-filter: blur(5px);
    box-shadow: 0 0 5px rgba(0, 0, 0, .7);
    transition: all .3s;

    &.force {
        left: 202px;
        top: 12px;
        background: transparent;
    }
}

.filter-bar-container {
    position: sticky;
    top: 0;
    z-index: 10;
    background: var(--sidebar-bg, #212120);
    width: calc(100% - 15px);
    margin-top: -25px;
    padding-top: 15px;
    padding-bottom: 15px;

    input {
        background: rgba(0, 0, 0, .95);
        -webkit-backdrop-filter: blur(5px);
        backdrop-filter: blur(5px);
        padding: 5px;
        width: calc(100% + 15px);
    }

    input:placeholder-shown {
        text-align: center;
    }

    input:focus:placeholder-shown {
        text-align: left;
    }
}

.wrapper.auto-mode .mobile-nav-toggle {
    top: 45px;

    &.force {
        top: 52px;
    }
}

@media screen and (min-width: 991px) {
    .mobile-nav-toggle {
        display: none;
    }
}

::v-deep(.sidebar .nav-selected) {
    background: rgba(0, 162, 255, 0.2);
    border-radius: 4px;
    
    > a {
        color: #00a2ff !important;
    }
}

::v-deep(.sidebar .nav-selected.active) {
    background: rgba(0, 162, 255, 0.3);
}
</style>
